import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PathEnum } from './modules/shared/enums/pathEnum';
import { Error404Component } from './modules/shared/pages/error/error-404/error-404.component';
import { RoleEnum } from './modules/shared/enums/roleEnum';
import { SubscriptionStatusEnum } from './modules/shared/enums/subscriptionStatusEnum';
import { TeamAuthGuard } from './core/auth/guards/team.auth.guard';
import { AffiliateAuthGuard } from './core/auth/guards/affiliate.auth.guard';
import { PlanEnum } from './modules/shared/enums/planEnum';

const defaultAppPath = 'signed-in-redirect';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: defaultAppPath },

    { path: PathEnum.APP, pathMatch: 'full', redirectTo: defaultAppPath },
    { path: PathEnum.APP_TEAM, pathMatch: 'full', data: { admin: true }, loadChildren: () => import('app/modules/shared/pages/signed-in-redirect/signed-in-redirect.routes') },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    // { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: defaultAppPath },

    // Auth routes for guests
    {
        path: '',
        component: LayoutComponent,
        canMatch: [NoAuthGuard],
        data: {
            layout: 'empty',
            allowedRoles: [RoleEnum.ALL]
        },
        children: [
            { path: PathEnum.AUTH_CONFIRMATION_REQUIRED, loadChildren: () => import('app/modules/partner/auth/confirmation-required/confirmation-required.routes') },
            { path: PathEnum.AUTH_VALIDATE_USER_EMAIL, loadChildren: () => import('app/modules/partner/auth/validate-user-email/validate-user-email.routes') },
            { path: PathEnum.AUTH_FORGOT_PASSWORD, loadChildren: () => import('app/modules/partner/auth/forgot-password/forgot-password.routes') },
            { path: PathEnum.AUTH_RESET_PASSWORD, loadChildren: () => import('app/modules/partner/auth/reset-password/reset-password.routes') },
            { path: PathEnum.AUTH_SIGN_IN, loadChildren: () => import('app/modules/partner/auth/sign-in/sign-in.routes') },
            { path: PathEnum.AUTH_SIGN_UP, loadChildren: () => import('app/modules/partner/auth/sign-up/sign-up.routes') },
            { path: PathEnum.AUTH_STEPPER_SIGN, loadChildren: () => import('app/modules/partner/auth/stepper-sign/stepper-sign.routes') },
            {
                path: PathEnum.SESSION,
                canMatch: [AuthGuard],
                data: {
                    layout: 'empty',
                    allowedRoles: [RoleEnum.ALL]
                },
                children: [
                    { path: PathEnum.SESSION_SIGN_OUT, loadChildren: () => import('app/modules/partner/auth/sign-out/sign-out.routes') },
                    { path: PathEnum.SESSION_UNLOCK_SESSION, loadChildren: () => import('app/modules/partner/auth/unlock-session/unlock-session.routes') }
                ]

            },
            // For team & partner users
            { path: PathEnum.AUTH_SIGNED_IN_REDIRECT, loadChildren: () => import('app/modules/shared/pages/signed-in-redirect/signed-in-redirect.routes') },

            {
                path: PathEnum.AUTH_WIZARD_SIGN,
                canMatch: [NoAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.ALL],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/partner/auth/wizard-sign/wizard-sign.routes')
            },
        ]
    },

    // Auth routes for authenticated partner users
    {
        path: PathEnum.APP,
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        canMatch: [AuthGuard],
        data: {
            allowedRoles: [RoleEnum.ALL] // Necessary to enter in the parent '/app'
        },
        children: [
            {
                path: PathEnum.APP_EXAMPLE,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/shared/pages/example/example.routes')
            },
            {
                path: PathEnum.APP_PARTNER_MENU,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                children: [
                    {
                        path: '',
                        loadChildren: () => import('app/modules/partner/pages/menu-partner/menu-partner.routes')
                    },
                    {
                        path: ':id/edit',
                        loadChildren: () => import('app/modules/partner/pages/menu-edit/menu-edit.routes')
                    }
                ]
            },
            {
                path: PathEnum.APP_PARTNER_QRCODE,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/partner/pages/partner-qrcode/partner-qrcode.routes')
            },
            {
                path: PathEnum.APP_PARTNER_ESTABLISHMENT,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/partner/pages/establishment-partner/establishment-partner.routes')
            },
            {
                path: PathEnum.APP_PARTNER_SETTINGS,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/partner/pages/settings-partner/settings-partner.routes')
            },
            {
                path: PathEnum.APP_PARTNER_CAMPAIGN_REVIEW,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.PSA, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.PRO
                },
                loadChildren: () => import('app/modules/partner/pages/campaign-review/campaign-review.routes')
            },
            {
                path: PathEnum.APP_PARTNER_DASHBOARD,
                canMatch: [AuthGuard],
                data: {
                    allowedRoles: [RoleEnum.APR, RoleEnum.PEM],
                    allowedSubscriptionStatus: [SubscriptionStatusEnum.ALL],
                    allowedPlans: PlanEnum.ALL
                },
                loadChildren: () => import('app/modules/partner/pages/dashboards-partner/dashboards-partner.routes')
            }
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: PathEnum.LANDING_PAGE, loadChildren: () => import('app/modules/landing/home/home.routes') },
            { path: PathEnum.APP_PRICES, loadChildren: () => import('app/modules/landing/components/modern-pricing/modern-pricing.routes') },
            { path: 'design-system', loadChildren: () => import('app/modules/shared/pages/design-system/design-system.routes') },
            // Public establishment deletion confirmation — reached from the email link.
            // No guard: token is the authorization. Works for both signed-in and signed-out
            // visitors so the owner can confirm even if their session expired in the meantime.
            {
                path: PathEnum.ESTABLISHMENT_DELETION_CONFIRM,
                loadChildren: () => import('app/modules/partner/auth/establishment-deletion-confirm/establishment-deletion-confirm.routes')
            },
        ]
    },

    // Admin routes
    // Before authentication
    {
        path: PathEnum.APP_TEAM,
        component: LayoutComponent,
        canMatch: [NoAuthGuard],
        data: {
            layout: 'empty',
            allowedRoles: [RoleEnum.ALL]
        },
        children: [
            { path: PathEnum.AUTH_FORGOT_PASSWORD, loadChildren: () => import('app/modules/team/auth/team-forgot-password/team-forgot-password.routes') },
            { path: PathEnum.AUTH_RESET_PASSWORD, loadChildren: () => import('app/modules/team/auth/team-reset-password/team-reset-password.routes') },
            { path: PathEnum.AUTH_SIGN_IN, loadChildren: () => import('app/modules/team/auth/team-sign-in/team-sign-in.routes') },
            {
                path: PathEnum.SESSION,
                canMatch: [TeamAuthGuard],
                data: {
                    layout: 'empty',
                    allowedRoles: [RoleEnum.ALL]
                },
                children: [
                    { path: PathEnum.SESSION_SIGN_OUT, loadChildren: () => import('app/modules/team/auth/team-sign-out/team-sign-out.routes') },
                    { path: PathEnum.SESSION_UNLOCK_SESSION, loadChildren: () => import('app/modules/team/auth/team-unlock-session/team-unlock-session.routes') }
                ]

            },
        ]
    },
    //After authentication
    {
        path: PathEnum.APP_TEAM,
        canMatch: [TeamAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            allowedRoles: [RoleEnum.ATR] // Necessary to enter in the parent '/app'
        },
        children: [
            { path: PathEnum.APP_EXAMPLE, loadChildren: () => import('app/modules/shared/pages/example/example.routes') },
            {
                path: PathEnum.APP_TEAM_DASHBOARD,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.ATR]
                },
                loadChildren: () => import('app/modules/team/pages/dashboards-team/dashboards-team.routes')
            },
            {
                path: PathEnum.APP_PARTNERS,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD, RoleEnum.TMA, RoleEnum.TSU, RoleEnum.TRO]
                },
                loadChildren: () => import('app/modules/team/pages/partners-list/partners-list.routes'),
            },
            {
                path: PathEnum.APP_PARTNERS + '/:id',
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD, RoleEnum.TMA, RoleEnum.TSU, RoleEnum.TRO]
                },
                loadChildren: () => import('app/modules/team/pages/partner-details/partner-details.routes'),
            },
            {
                path: PathEnum.APP_TEAM_AFFILIATE_APPLICATIONS,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/affiliate-applications/affiliate-applications.routes'),
            },
            {
                path: PathEnum.APP_TEAM_AFFILIATES,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/affiliates-list/affiliates-list.routes'),
            },
            {
                path: PathEnum.APP_TEAM_AFFILIATES + '/:id',
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/affiliate-details/affiliate-details.routes'),
            },
            {
                path: PathEnum.APP_TEAM_AFFILIATE_PAYOUTS,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/affiliate-payouts/affiliate-payouts.routes'),
            },
            {
                path: PathEnum.APP_TEAM_WHATSAPP,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/whatsapp/whatsapp.routes')
            },
            {
                path: PathEnum.APP_TEAM_PARTNER_TASKS,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/partner-tasks/partner-tasks.routes'),
            },
            {
                path: PathEnum.APP_TEAM_SETTINGS,
                canMatch: [TeamAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.TSA, RoleEnum.TAD]
                },
                loadChildren: () => import('app/modules/team/pages/settings-team/settings-team.routes')
            }
        ]
    },
    // Affiliate routes — pre-auth (sign-in / forgot / reset).
    // Dedicated /affiliate parent path with its own auth chain; sibling of /admin.
    {
        path: PathEnum.APP_AFFILIATE,
        component: LayoutComponent,
        canMatch: [NoAuthGuard],
        data: {
            layout: 'empty',
            allowedRoles: [RoleEnum.ALL]
        },
        children: [
            { path: PathEnum.APP_AFFILIATE_SIGN_IN, loadChildren: () => import('app/modules/affiliate/auth/affiliate-sign-in/affiliate-sign-in.routes') },
            { path: PathEnum.APP_AFFILIATE_FORGOT_PASSWORD, loadChildren: () => import('app/modules/affiliate/auth/affiliate-forgot-password/affiliate-forgot-password.routes') },
            { path: PathEnum.APP_AFFILIATE_RESET_PASSWORD, loadChildren: () => import('app/modules/affiliate/auth/affiliate-reset-password/affiliate-reset-password.routes') },
            {
                path: PathEnum.SESSION,
                canMatch: [AffiliateAuthGuard],
                data: {
                    layout: 'empty',
                    allowedRoles: [RoleEnum.ALL]
                },
                children: [
                    { path: PathEnum.APP_AFFILIATE_SIGN_OUT, loadChildren: () => import('app/modules/affiliate/auth/affiliate-sign-out/affiliate-sign-out.routes') },
                ]
            },
        ]
    },
    // Affiliate self-portal — gated to ROLE_AFFILIATE only.
    {
        path: PathEnum.APP_AFFILIATE,
        canMatch: [AffiliateAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // Force the vertical sidebar layout — affiliates have only 6 items, easier
            // to navigate as a sidebar than as a top horizontal nav (the global default).
            layout: 'classy',
            allowedRoles: [RoleEnum.AFF]
        },
        children: [
            {
                path: PathEnum.APP_AFFILIATE_PORTAL,
                canMatch: [AffiliateAuthGuard],
                data: {
                    allowedRoles: [RoleEnum.AFF]
                },
                children: [
                    { path: '', pathMatch: 'full', redirectTo: PathEnum.APP_AFFILIATE_PORTAL_DASHBOARD },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_DASHBOARD,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-dashboard/affiliate-dashboard.routes')
                    },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_LINK,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-link/affiliate-link.routes')
                    },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_REFERRED,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-referred/affiliate-referred.routes')
                    },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_COMMISSIONS,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-my-commissions/affiliate-my-commissions.routes')
                    },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_PAYOUTS,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-my-payouts/affiliate-my-payouts.routes')
                    },
                    {
                        path: PathEnum.APP_AFFILIATE_PORTAL_SETTINGS,
                        canMatch: [AffiliateAuthGuard],
                        data: { allowedRoles: [RoleEnum.AFF] },
                        loadChildren: () => import('app/modules/affiliate/pages/affiliate-settings/affiliate-settings.routes')
                    }
                ]
            }
        ]
    },

    // Error routes
    {
        path: PathEnum.ERROR,
        canMatch: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: PathEnum.ERROR_403, loadChildren: () => import('app/modules/shared/pages/error/error-403/error-403.routes') },
            { path: PathEnum.ERROR_404, loadChildren: () => import('app/modules/shared/pages/error/error-404/error-404.routes') },
            { path: PathEnum.ERROR_500, loadChildren: () => import('app/modules/shared/pages/error/error-500/error-500.routes') }
        ]
    },

    // Page not found
    { path: '**', pathMatch: 'full', component: Error404Component },

    {
        path: 'partner',
        children: [
            {
                path: 'menus',
                children: [
                    {
                        path: '',
                        loadChildren: () => import('./modules/partner/pages/menu-partner/menu-partner.routes'),
                    },
                    {
                        path: ':id/edit',
                        loadChildren: () => import('./modules/partner/pages/menu-edit/menu-edit.routes'),
                    }
                ]
            }
        ]
    }

];
